import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Send, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import type { LandingSectionEditPatch, VoiceResearchMessage } from "@/lib/api";
import SaveOperatorInsightButton from "@/components/operator/SaveOperatorInsightButton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type Surface = "comments_chat" | "messenger_chat" | "landings_chat" | "inspiration_chat";

type Props = {
  surface: Surface;
  className?: string;
  pageContext?: Record<string, unknown> | null;
  /** Po zapisie lokalnego HTML (Landingi): odśwież iframe / komunikat. */
  onLocalHtmlApplied?: (note?: string) => void;
  /** Kontrolowane pending edits (Landingi). Bez propsów: stan wewnętrzny. */
  pendingEdits?: LandingSectionEditPatch[];
  onPendingEditsChange?: (edits: LandingSectionEditPatch[]) => void;
};

const COPY: Record<
  Surface,
  { title: string; subtitle: string; empty: string; placeholder: string }
> = {
  comments_chat: {
    title: "Chat głosu",
    subtitle: "Sam odpyta bazę. Raporty i rekomendacje.",
    empty:
      "Zapytaj np. o przeciążenie opieką, lęk przed matką, brak wsparcia. Mogę szukać semantycznie i po słowach.",
    placeholder: "Napisz pytanie…",
  },
  messenger_chat: {
    title: "Chat głosu",
    subtitle: "Sam odpyta bazę. Raporty i rekomendacje.",
    empty:
      "Zapytaj o historie DM, tematy rozmów, wzorce w Messengerze. Mogę szukać w historiach i surowych wiadomościach.",
    placeholder: "Napisz pytanie…",
  },
  landings_chat: {
    title: "Chat landingu",
    subtitle: "Usprawnienia sekcji, CTA, copy. Kontekst z wybranej strony.",
    empty:
      "Wybierz sekcję po lewej i zapytaj np. o drop, CTA albo wariant copy. Możesz też omówić całą stronę.",
    placeholder: "Co poprawić na tej stronie / sekcji…",
  },
  inspiration_chat: {
    title: "Chat inspiracji",
    subtitle: "Omów wzorzec, ton, CTA. Kontekst z otwartej strony.",
    empty:
      "Wybierz link po lewej i zapytaj np. o strukturę hero, proof albo co przenieść na nasze landingi.",
    placeholder: "Co warto wziąć z tej strony…",
  },
};

export default function VoiceResearchChat({
  surface,
  className,
  pageContext,
  onLocalHtmlApplied,
  pendingEdits: pendingEditsProp,
  onPendingEditsChange,
}: Props) {
  const qc = useQueryClient();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [wider, setWider] = useState(false);
  const [autoWiderNote, setAutoWiderNote] = useState(false);
  const [pendingEditsInternal, setPendingEditsInternal] = useState<LandingSectionEditPatch[]>(
    [],
  );
  const pendingControlled = typeof onPendingEditsChange === "function";
  const pendingEdits = pendingControlled
    ? pendingEditsProp || []
    : pendingEditsInternal;
  const setPendingEdits = (edits: LandingSectionEditPatch[]) => {
    if (pendingControlled) onPendingEditsChange?.(edits);
    else setPendingEditsInternal(edits);
  };
  const [applyNote, setApplyNote] = useState<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const copy = COPY[surface];

  const contextKey = useMemo(() => {
    if (!pageContext) return "";
    if (surface === "landings_chat") {
      return String(pageContext.page_id || "").trim();
    }
    if (surface === "inspiration_chat") {
      return String(
        pageContext.link_id || pageContext.id || pageContext.url || "",
      ).trim();
    }
    return "";
  }, [surface, pageContext]);

  const pageLabel = useMemo(() => {
    if (!pageContext) return "";
    if (surface === "landings_chat") {
      return String(pageContext.label || pageContext.page_id || "").trim();
    }
    if (surface === "inspiration_chat") {
      return String(pageContext.title || pageContext.id || "").trim();
    }
    return "";
  }, [surface, pageContext]);

  const scopedByPage =
    surface === "landings_chat" || surface === "inspiration_chat";

  const threadsQ = useQuery({
    queryKey: ["voice-research-threads", surface, contextKey],
    queryFn: () =>
      api.voiceResearchThreads(
        surface,
        scopedByPage ? contextKey || undefined : undefined,
      ),
    enabled: !scopedByPage || Boolean(contextKey),
  });

  // Osobna konwersacja na podstronę: przy zmianie page_id wyczyść lokalny wybór wątku.
  useEffect(() => {
    if (!scopedByPage) return;
    setThreadId(null);
    setPendingEdits([]);
    setApplyNote(null);
    setCanUndo(false);
    // pendingControlled: clear parent too
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey, scopedByPage]);

  useEffect(() => {
    const items = threadsQ.data?.items || [];
    if (!threadId && items[0]?.id) setThreadId(items[0].id);
  }, [threadsQ.data, threadId, contextKey]);

  const threadQ = useQuery({
    queryKey: ["voice-research-thread", threadId],
    queryFn: () => api.voiceResearchThread(threadId!),
    enabled: Boolean(threadId),
  });

  const messages: VoiceResearchMessage[] = threadQ.data?.messages || [];

  useEffect(() => {
    if (surface !== "landings_chat") return;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role !== "assistant") continue;
      const edits = m.meta?.pending_edits;
      if (Array.isArray(edits) && edits.length) {
        setPendingEdits(edits);
        return;
      }
      break;
    }
  }, [messages, surface]);

  const chatM = useMutation({
    mutationFn: (message: string) =>
      api.voiceResearchChat({
        message,
        thread_id: threadId,
        surface,
        wider_context: wider,
        page_context:
          surface === "landings_chat" || surface === "inspiration_chat"
            ? pageContext || null
            : undefined,
      }),
    onSuccess: (res) => {
      setThreadId(res.thread_id);
      if (res.auto_wider) {
        setWider(true);
        setAutoWiderNote(true);
      }
      const fromRes = res.pending_edits || res.message?.meta?.pending_edits;
      if (Array.isArray(fromRes) && fromRes.length) {
        setPendingEdits(fromRes);
        setApplyNote(null);
      }
      void qc.setQueryData(["voice-research-thread", res.thread_id], {
        thread: {
          id: res.thread_id,
          surface,
          title: pageLabel || "Rozmowa",
          context_key: contextKey,
        },
        messages: res.messages,
      });
      void qc.invalidateQueries({
        queryKey: ["voice-research-threads", surface, contextKey],
      });
    },
  });

  const newThreadM = useMutation({
    mutationFn: () =>
      api.voiceResearchCreateThread(
        surface,
        pageLabel ? `${pageLabel}` : "Nowa rozmowa",
        scopedByPage ? contextKey : "",
      ),
    onSuccess: (t) => {
      setThreadId(t.id);
      setPendingEdits([]);
      setApplyNote(null);
      void qc.invalidateQueries({
        queryKey: ["voice-research-threads", surface, contextKey],
      });
    },
  });

  const pageId = String(pageContext?.page_id || "").trim();
  const sectionId = String(pageContext?.selected_section || "").trim();

  const applyM = useMutation({
    mutationFn: () =>
      api.landingSectionEditsApply({
        page_id: pageId,
        section_id: sectionId,
        patches: pendingEdits.map((p) => ({
          old_text: p.old_text,
          new_text: p.new_text,
          note: p.note || "",
        })),
      }),
    onSuccess: (res) => {
      setPendingEdits([]);
      setCanUndo(true);
      const note =
        res.note_pl ||
        "Zapisano na serwerze panelu. Podgląd odświeżony. Live: użyj Wgraj live.";
      setApplyNote(note);
      onLocalHtmlApplied?.(note);
    },
  });

  const undoM = useMutation({
    mutationFn: () => api.landingSectionEditsUndo({ page_id: pageId }),
    onSuccess: (res) => {
      setCanUndo(false);
      const note = res.note_pl || "Cofnięto ostatni zapis.";
      setApplyNote(note);
      onLocalHtmlApplied?.(note);
    },
  });

  const insightMessages = useMemo(
    () =>
      messages.map((m) => ({
        role: m.role,
        content: m.content,
      })),
    [messages],
  );

  const focusLabel = useMemo(() => {
    if (!pageContext) return null;
    if (surface === "landings_chat") {
      const section = String(pageContext.selected_section || "").trim();
      const label = String(pageContext.label || pageContext.page_id || "").trim();
      if (section) return `Fokus: #${section}`;
      if (label) return `Strona: ${label}`;
      return null;
    }
    if (surface === "inspiration_chat") {
      const title = String(pageContext.title || "").trim();
      if (title) return `Strona: ${title}`;
    }
    return null;
  }, [surface, pageContext]);

  const send = () => {
    const text = draft.trim();
    if (!text || chatM.isPending) return;
    setDraft("");
    chatM.mutate(text);
  };

  const canApply =
    surface === "landings_chat" &&
    pendingEdits.length > 0 &&
    Boolean(pageId && sectionId) &&
    !applyM.isPending;

  return (
    <aside
      className={cn(
        "flex h-full min-h-0 w-full flex-col border-0 bg-white max-lg:min-h-[28rem] lg:rounded-none",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-3 py-2.5">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{copy.title}</h2>
          <p className="text-[11px] text-slate-500">{copy.subtitle}</p>
          {focusLabel ? (
            <p className="mt-1 text-[11px] font-medium text-slate-700">{focusLabel}</p>
          ) : null}
        </div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-8 px-2"
          onClick={() => newThreadM.mutate()}
          disabled={newThreadM.isPending || (scopedByPage && !contextKey)}
          title="Nowy wątek"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-3 py-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
          <input
            type="checkbox"
            className="rounded border-slate-300"
            checked={wider}
            onChange={(e) => {
              setWider(e.target.checked);
              setAutoWiderNote(false);
            }}
          />
          Szerszy kontekst
        </label>
        {autoWiderNote && wider && (
          <span className="text-[10px] text-amber-700">włączone na tę rozmowę</span>
        )}
        <SaveOperatorInsightButton
          surface={surface}
          sourceRef={threadId || ""}
          messages={insightMessages}
          disabled={!messages.length}
          className="ml-auto h-7 text-xs"
        />
      </div>

      {(threadsQ.data?.items?.length || 0) > 1 && (
        <div className="border-b border-slate-100 px-3 py-1.5">
          <select
            className="w-full rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs"
            value={threadId || ""}
            onChange={(e) => setThreadId(e.target.value || null)}
          >
            {(threadsQ.data?.items || []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.title || t.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
        {!messages.length && !chatM.isPending && (
          <p className="text-sm text-slate-500">{copy.empty}</p>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            className={cn(
              "rounded-lg px-2.5 py-2 text-sm",
              m.role === "user" ? "ml-6 bg-slate-900 text-white" : "mr-2 bg-slate-50 text-slate-800",
            )}
          >
            <div className="whitespace-pre-wrap">{m.content}</div>
            {m.role === "assistant" && m.meta?.tool_trace && m.meta.tool_trace.length > 0 && (
              <details className="mt-2 text-[10px] text-slate-500">
                <summary className="cursor-pointer">Narzędzia ({m.meta.tool_trace.length})</summary>
                <ul className="mt-1 list-disc pl-4">
                  {m.meta.tool_trace.map((t, i) => (
                    <li key={`${t.name}-${i}`}>
                      {t.name}
                      {t.ok === false ? " (błąd)" : ""}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {m.role === "assistant" && m.meta?.sources && m.meta.sources.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {m.meta.sources.slice(0, 4).map((s, i) => (
                  <span
                    key={`${s.id || s.text}-${i}`}
                    className="max-w-full truncate rounded bg-white px-1.5 py-0.5 text-[10px] text-slate-600 ring-1 ring-slate-200"
                    title={s.text}
                  >
                    {(s.text || "").slice(0, 48)}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
        {chatM.isPending && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {surface === "landings_chat"
              ? "Układam rekomendacje…"
              : "Szukam w bazie i układam odpowiedź…"}
          </div>
        )}
        {chatM.isError && (
          <p className="text-sm text-red-600">{(chatM.error as Error)?.message || "Błąd chatu"}</p>
        )}

        {surface === "landings_chat" && pendingEdits.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-2.5">
            <p className="text-xs font-semibold text-amber-950">Propozycja zmian HTML</p>
            <p className="mt-0.5 text-[10px] text-amber-800">
              Zapis na serwerze panelu (podgląd Serwer). Na kasiazareba.pl dopiero po „Wgraj live”.
            </p>
            {!sectionId && (
              <p className="mt-1 text-[11px] text-rose-700">Wybierz sekcję po lewej, żeby zatwierdzić.</p>
            )}
            <ul className="mt-2 space-y-2">
              {pendingEdits.map((p, i) => (
                <li
                  key={p.id || `${i}-${p.old_text.slice(0, 12)}`}
                  className="rounded-md bg-white/80 px-2 py-1.5 text-[11px] ring-1 ring-amber-100"
                >
                  {p.note ? <p className="mb-1 font-medium text-slate-700">{p.note}</p> : null}
                  <p className="text-rose-700 line-through">{p.old_text}</p>
                  <p className="mt-0.5 text-emerald-800">{p.new_text}</p>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                className="h-7 text-xs"
                disabled={!canApply}
                onClick={() => applyM.mutate()}
              >
                {applyM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Zastosuj"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                disabled={applyM.isPending}
                onClick={() => {
                  setPendingEdits([]);
                  setApplyNote(null);
                }}
              >
                Odrzuć
              </Button>
            </div>
            {applyM.isError && (
              <p className="mt-1 text-[11px] text-rose-700">
                {(applyM.error as Error)?.message || "Nie udało się zapisać"}
              </p>
            )}
          </div>
        )}

        {surface === "landings_chat" && applyNote && (
          <div className="rounded-md bg-slate-50 px-2.5 py-2 text-[11px] text-slate-700 ring-1 ring-slate-200">
            <p>{applyNote}</p>
            {canUndo && pageId ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="mt-1 h-7 px-2 text-xs"
                disabled={undoM.isPending}
                onClick={() => undoM.mutate()}
              >
                {undoM.isPending ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Undo2 className="mr-1 h-3.5 w-3.5" />
                )}
                Cofnij ostatni zapis
              </Button>
            ) : null}
            {undoM.isError && (
              <p className="mt-1 text-rose-700">{(undoM.error as Error)?.message || "Undo nieudany"}</p>
            )}
          </div>
        )}
      </div>

      <form
        className="flex items-end gap-2 border-t border-slate-100 p-2"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder={copy.placeholder}
          rows={3}
          disabled={chatM.isPending}
          className="min-h-[4.5rem] w-full flex-1 resize-y rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        />
        <Button type="submit" size="sm" disabled={chatM.isPending || !draft.trim()}>
          {chatM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </aside>
  );
}
